import { createConsumer, ActionCableConsumer } from '@anycable/web';
import ActionCableLink from 'graphql-ruby-client/subscriptions/ActionCableLink';
import { logger } from '@lib/services/logger';

type CreateCableLinkArgs = {
  authScope: string;
  // Prefixes the transport's log lines to name the app they came from, since
  // they are emitted by this shared module. It cannot name the feature: there is
  // one link (and one consumer) per app, and every subscription in that app —
  // AI turns, budget, job tracker, coverage — shares it, so a per-feature label
  // would have to be carried on the channel, not here.
  label?: string;
};

let consumer: ActionCableConsumer | undefined;

// AnyCable carries the cause in `ReasonError.reason` (`transport_closed`,
// `server_restart`, `unauthorized`, …); `message` is the constant `'Disconnected'`
// or empty for most of those, so it is only a fallback.
const getDropReason = (error?: { reason?: string; message?: string }) =>
  error?.reason || error?.message || undefined;

// The BE serves subscriptions over the ActionCable protocol (graphql-ruby
// GraphqlChannel), not GraphQL-over-WebSocket — so this stays an ActionCableLink even
// though the server is AnyCable. `@anycable/web`'s createConsumer is a drop-in for
// `@rails/actioncable`'s, with better reconnection (exponential backoff + jitter).
//
// VITE_WS_URL is the full endpoint including path, because AnyCable runs on its own
// host (cable.<zone>) rather than under the API — deriving it from VITE_API_URL would
// silently produce a URL that resolves to Rails and 404s. The handshake authenticates
// from the session cookie selected by the `auth_scope` query param; the cookie is
// scoped to the parent domain, so it reaches the cable host too.
export function getCableConsumer(
  authScope: string,
  label = 'Cable',
): ActionCableConsumer {
  if (!consumer) {
    const wsUrl = import.meta.env.VITE_WS_URL;
    if (!wsUrl) {
      logger.error(
        `${label}: VITE_WS_URL is not set — subscriptions cannot connect`,
      );
    }
    const cableUrl = `${wsUrl}?auth_scope=${encodeURIComponent(authScope)}`;
    logger.debug(`${label}: creating AnyCable consumer`, { cableUrl });
    consumer = createConsumer(cableUrl);

    // A dropped socket is worth a warning in every app that has one: no app
    // recovers from it here (AnyCable reconnects on its own, and in-flight AI
    // turns are recovered by the inactivity poll in useAiConversation), but a
    // drop nobody notices leaves whatever the subscription feeds frozen on stale
    // data — the symptom every one of these subscriptions exists to remove. So
    // it is logged here rather than passed back to each app to log identically.
    //
    // These are transport-level events, deliberately NOT the channel's
    // `disconnected` callback below: that one also fires on an ordinary Apollo
    // unsubscribe (React deletes the subtree on navigation -> unsubscribe ->
    // channel close), so routing the warning through it reported every navigation
    // away from a subscribed page as a lost socket. These fire only for the
    // socket itself — `disconnect` when it drops and a reconnect is coming,
    // `close` when it gives up — and the two mean different things to whoever
    // reads them, so they stay separate messages. One incident can produce both.
    consumer.cable.on('disconnect', (error) => {
      logger.warn(`${label}: socket dropped, reconnecting`, {
        reason: getDropReason(error),
      });
    });
    consumer.cable.on('close', (error) => {
      logger.warn(`${label}: socket closed, no further reconnects`, {
        reason: getDropReason(error),
      });
    });
  }
  return consumer;
}

export default function createCableLink({
  authScope,
  label = 'Cable',
}: CreateCableLinkArgs) {
  const cableConsumer = getCableConsumer(authScope, label);

  return new ActionCableLink({
    // ActionCableLink nominally types `cable` as @rails/actioncable's Consumer.
    // AnyCable's ActionCableConsumer exposes the same `subscriptions.create` surface
    // (and the same connected/received/disconnected/rejected callbacks) but is a
    // distinct type, so the structural match has to be asserted.
    cable: cableConsumer as unknown as ConstructorParameters<
      typeof ActionCableLink
    >[0]['cable'],
    callbacks: {
      connected: () => {
        logger.debug(`${label}: cable channel connected`);
      },
      disconnected: () => {
        logger.debug(`${label}: cable channel disconnected`);
      },
      rejected: () => {
        logger.warn(`${label}: cable channel rejected (auth scope mismatch?)`);
      },
    },
  });
}
